import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { ApiError } from '@/services/errorHandler';

interface RateLimitDialogProps {
  open: boolean;
  onClose: () => void;
  error: ApiError | null;
  onRetry?: () => void;
}

export default function RateLimitDialog({ open, onClose, error, onRetry }: RateLimitDialogProps) {
  const [timeRemaining, setTimeRemaining] = useState(error?.retryAfter || 60);
  const [canRetry, setCanRetry] = useState(false);

  // Update timeRemaining when error changes
  useEffect(() => {
    if (error?.retryAfter) {
      setTimeRemaining(error.retryAfter);
      setCanRetry(false);
    }
  }, [error?.retryAfter]);

  useEffect(() => {
    if (!open || !error?.isRateLimit) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          setCanRetry(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open, error?.isRateLimit]);

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    }
    onClose();
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${remainingSeconds}s`;
  };

  // Don't render if no error
  if (!error) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {error.isRateLimit ? 'Too Many Requests' : 'Request Failed'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Alert severity={error.isRateLimit ? 'warning' : 'error'} sx={{ mb: 2 }}>
            {error.message}
          </Alert>
          
          {error.isRateLimit && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" gutterBottom>
                Please wait before trying again
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="h4" color="primary">
                  {formatTime(timeRemaining)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        {canRetry && onRetry && (
          <Button onClick={handleRetry} variant="contained" color="primary">
            Try Again
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
} 